import type { SlotSequence } from './types';

/**
 * CACHE DA SEQUÊNCIA DE SLOTS — tira o round-trip de sync do caminho de playback no boot.
 *
 * <p>O PROBLEMA MEDIDO (QB50B, Tizen 6.5, 2026-07-28). A `slot_sequence` só existia em memória:
 * vinha de uma resposta de sync e nada a persistia. Então **todo** boot — inclusive cada reload do
 * refresh periódico — precisava completar uma ida à rede antes de o player saber o que tocar.
 * Orçamento do preto medido em 8 boots: `react` em 1006ms, `playlist` em **2289ms**. Ou seja
 * **1283ms** de tela apagada esperando a rede, num aparelho cuja mídia já está toda em disco.
 *
 * <p>Também fecha metade do mandato offline-first do Renato (`.claude/rules/cache-offline-first.md`,
 * R3): "ao ligar sem internet, o player deve restaurar a última programação do disco e voltar a
 * tocar — sem depender de um sync bem-sucedido no boot". A mídia já era cacheada; a PROGRAMAÇÃO
 * não. Uma TV que religava sem internet não sabia o que tocar.
 *
 * <p>SEM TETO DE IDADE (Renato 2026-11-04 — offline-first independente do tempo offline). A posição
 * no loop não vem mais da janela horária (`scheduledOffsetS` hoje só alimenta telemetria): vem da
 * ÉPOCA canônica (`playback_started_at`), gravada junto. Então uma sequência de ontem — ou de
 * semana passada — continua ancorando certo, e o player que religa depois de muito tempo desligado
 * sobe em fase com o grupo. Quem decide se um item toca é a VALIDADE do conteúdo (validUntil +
 * janelas, filtrada no player pelas 6 camadas AND) e a presença da mídia em disco (item sem cache é
 * pulado), NUNCA a idade do atalho. A única guarda que resta é relógio-pra-trás (age < 0 = escrita
 * corrompida/NTP). O sync roda logo atrás e sobrepõe tudo pelo caminho normal (`setFromSync`).
 *
 * <p>NÃO substitui o sync: é um atalho para o PRIMEIRO frame. O sync roda logo atrás e a resposta
 * dele sobrepõe a sequência restaurada pelo caminho normal (`setFromSync`).
 */

const KEY = 'efflux_slot_sequence_v1';

interface CachedSlotSequence {
  /** Quando foi gravada (epoch). */
  savedAt: number;
  /** Tier da sequência — precisa voltar junto, senão o Stream não sabe em que modo está. */
  tier: 2 | 3;
  /**
   * A época canônica (`playback_started_at`) vigente quando gravamos.
   *
   * <p>Precisa viajar junto com a sequência: é ela quem diz se o boot pode ancorar os slots no
   * relógio ou se deve começar no slot 0 como sempre. Sem isto, uma TV de
   * videowall que reinicia volta fora de fase até o primeiro sync chegar — justamente o buraco
   * que o cache existe pra fechar. Ausente (cache de versão anterior) = sem época: o boot não ancora e espera o sync.
   */
  canonicalEpoch?: string | null;
  seq: SlotSequence;
}

/** Grava a sequência ativa. Best-effort — nunca pode quebrar playback. */
export function persistSlotSequence(
  seq: SlotSequence | null,
  tier: 2 | 3 | null,
  canonicalEpoch: string | null = null,
  now: number = Date.now(),
): void {
  try {
    // Sequência vazia não serve de atalho (o boot cairia em NoContent) — e apagar aqui evitaria
    // ressuscitar uma programação que o server já retirou. Melhor não gravar e manter a anterior:
    // `setFromSync` já trata slots vazios como transiente (race de orientação/emergência).
    if (!seq || !tier || seq.slots.length === 0) return;
    const payload: CachedSlotSequence = {
      savedAt: now,
      tier,
      canonicalEpoch,
      seq,
    };
    window.localStorage?.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* cheio/indisponível — o boot só perde o atalho */
  }
}

/**
 * A decisão de aproveitar o cache, PURA (testável sem localStorage/relógio).
 *
 * <p>SEM teto de idade (Renato 2026-11-04): a última programação restaurada vale por quanto tempo
 * ficar offline — quem corta item vencido/fora de janela é o filtro de validade no player, e item
 * sem mídia em disco é pulado (best-effort). A única guarda que resta é idade NEGATIVA: o relógio
 * da TV andou pra trás (NTP) e o cache ficou "do futuro" → escrita suspeita, não usa.
 */
export function isCachedSequenceUsable(
  cached: CachedSlotSequence | null,
  now: number,
): boolean {
  if (!cached) return false;
  if (!cached.seq || cached.seq.slots.length === 0) return false;
  if (cached.tier !== 2 && cached.tier !== 3) return false;
  const age = now - cached.savedAt;
  return age >= 0;
}

/**
 * Lê a sequência do disco se ela ainda for utilizável AGORA. `null` = boot segue esperando o sync
 * (comportamento antigo, sempre correto).
 */
export function readUsableSlotSequence(
  now: number = Date.now(),
): { seq: SlotSequence; tier: 2 | 3; canonicalEpoch: string | null } | null {
  try {
    const raw = window.localStorage?.getItem(KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedSlotSequence;
    if (!isCachedSequenceUsable(cached, now)) return null;
    return {
      seq: cached.seq,
      tier: cached.tier,
      canonicalEpoch:
        typeof cached.canonicalEpoch === 'string'
          ? cached.canonicalEpoch
          : null,
    };
  } catch {
    return null; // JSON corrompido → ignora (nunca adivinha programação)
  }
}

/** Só pra teste / limpeza de tenant. */
export function clearSlotSequenceCache(): void {
  try {
    window.localStorage?.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export const SLOT_SEQUENCE_CACHE_KEY = KEY;
export type { CachedSlotSequence };
